import { supabase } from '@/integrations/supabase/client';

export interface TranslationRequest {
  message: string;
  targetLanguage: string;
  websiteId?: string;
  websiteUrl?: string;
  websiteAnalysisData?: {
    website_content?: any[];
    website_info?: any;
    key_content_pages?: any[];
    post_ideas?: any[];
    language?: string;
  };
}

export interface TranslationResponse {
  success: boolean;
  translatedMessage: string;
  error?: string;
}

/**
 * Translates an English message to the user's language using AI
 */
export async function translateMessage(request: TranslationRequest): Promise<TranslationResponse> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        website_id: request.websiteId,
        website_url: request.websiteUrl || localStorage.getItem('onboardingWebsite') || undefined,
        message: `Write a language detection message directly in the user's language (${request.targetLanguage}). The message should say that you can see their website is in that language and that you're switching to communicate in their language. Include both the English name and native name of the language (e.g., "Danish", "Deutsch"). Make it conversational and natural. Respond with CHAT_MODE: followed by the message.`,
        history: [],
        website_analysis_data: request.websiteAnalysisData
      })
    });

    const data = await response.json();
    const reply: string = data?.reply || request.message;
    
    // Parse mode indicator from AI response
    let translatedMessage = reply;
    if (reply.startsWith('CHAT_MODE:')) {
      translatedMessage = reply.replace(/^CHAT_MODE:\s*/, '');
    }
    
    return {
      success: true,
      translatedMessage: translatedMessage.trim()
    };
  } catch (error) {
    console.error('Translation error:', error);
    return {
      success: false,
      translatedMessage: request.message, // Fallback to original message
      error: error instanceof Error ? error.message : 'Translation failed'
    };
  }
}

/**
 * Gets the flag emoji for a language code
 */
export function getLanguageFlag(languageCode: string): string {
  const languageToFlag: Record<string, string> = {
    'da': '🇩🇰', 'de': '🇩🇪', 'fr': '🇫🇷', 'es': '🇪🇸', 'it': '🇮🇹',
    'nl': '🇳🇱', 'sv': '🇸🇪', 'no': '🇳🇴', 'fi': '🇫🇮', 'pt': '🇵🇹',
    'pl': '🇵🇱', 'ru': '🇷🇺', 'ja': '🇯🇵', 'ko': '🇰🇷', 'zh': '🇨🇳', 'ar': '🇦🇪'
  };
  return languageToFlag[languageCode] || '';
}

